import { DesignDialog as Dialog } from "../../design/DesignDialog.js";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

export interface TerminalPortContextMenuState {
  anchor: HTMLElement;
  portId: string;
  label: string;
}

export interface TerminalPortContextMenuLabels {
  renamePort: string;
  deletePort: string;
  portLabel: string;
  portLabelPlaceholder: string;
  confirmRenamePort: string;
  cancel: string;
}

export interface TerminalPortContextMenuProps {
  portMenu: TerminalPortContextMenuState | null;
  labels: TerminalPortContextMenuLabels;
  onClose: () => void;
  onRename: (portId: string, label: string) => void;
  onRemove: (portId: string) => void;
  validateLabel?: (portId: string, label: string) => string | undefined;
}

/**
 * Context menu for scene graph terminal ports (rename + delete) with a label dialog.
 */
export const TerminalPortContextMenu = ({
  portMenu,
  labels,
  onClose,
  onRename,
  onRemove,
  validateLabel,
}: TerminalPortContextMenuProps) => {
  const [renameOpen, setRenameOpen] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");

  useEffect(() => {
    if (!portMenu) {
      setRenameOpen(false);
      setDraftLabel("");
      return;
    }
    setDraftLabel(portMenu.label);
  }, [portMenu]);

  const handleCloseAll = useCallback(() => {
    setRenameOpen(false);
    onClose();
  }, [onClose]);

  const handleOpenRename = useCallback(() => {
    if (!portMenu) return;
    setDraftLabel(portMenu.label);
    setRenameOpen(true);
  }, [portMenu]);

  const handleConfirmRename = useCallback(() => {
    if (
      !portMenu ||
      !draftLabel.trim() ||
      validateLabel?.(portMenu.portId, draftLabel.trim())
    )
      return;
    onRename(portMenu.portId, draftLabel.trim());
    handleCloseAll();
  }, [draftLabel, handleCloseAll, onRename, portMenu, validateLabel]);

  return (
    <>
      <Menu
        onMouseDown={(event) => event.stopPropagation()}
        anchorEl={portMenu?.anchor ?? null}
        open={Boolean(portMenu) && !renameOpen}
        onClose={handleCloseAll}
      >
        <MenuItem onClick={handleOpenRename} sx={{ fontSize: 12 }}>
          {labels.renamePort}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (portMenu) onRemove(portMenu.portId);
            handleCloseAll();
          }}
          sx={{ fontSize: 12, color: "error.main" }}
        >
          {labels.deletePort}
        </MenuItem>
      </Menu>
      <Dialog
        onMouseDown={(event) => event.stopPropagation()}
        open={renameOpen}
        onClose={handleCloseAll}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14 }}>{labels.portLabel}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={draftLabel}
            error={
              !!(
                portMenu && validateLabel?.(portMenu.portId, draftLabel.trim())
              )
            }
            helperText={
              portMenu
                ? validateLabel?.(portMenu.portId, draftLabel.trim())
                : undefined
            }
            placeholder={labels.portLabelPlaceholder}
            onChange={(event) => setDraftLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleConfirmRename();
              }
            }}
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAll}>{labels.cancel}</Button>
          <Button
            variant="contained"
            onClick={handleConfirmRename}
            disabled={
              !draftLabel.trim() ||
              !!(
                portMenu && validateLabel?.(portMenu.portId, draftLabel.trim())
              )
            }
          >
            {labels.confirmRenamePort}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
